import React, { useMemo } from 'react';
import { useTable } from 'react-table';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import IconButton from '@material-ui/core/IconButton';
import moment from 'moment';
import StatusBadge from './StatusBadge';
import { formatLatency } from './utils';
import { useStyles } from './styles';

const MessageTable = ({
  data,
  totalCount,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  loading,
}) => {
  const classes = useStyles();

  const columns = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'created_at',
        Cell: ({ value }) => moment(value).format('DD MMM YYYY, HH:mm:ss'),
      },
      {
        Header: 'Direction',
        accessor: 'direction',
        Cell: ({ value }) =>
          value ? value.charAt(0).toUpperCase() + value.slice(1) : '–',
      },
      {
        Header: 'Flow Type',
        accessor: 'flow_type',
        Cell: ({ value }) => value || '–',
      },
      {
        Header: 'Endpoint',
        accessor: 'endpoint',
        Cell: ({ value }) =>
          value ? (
            <Tooltip title={value} placement='top'>
              <span className={classes.truncatedCell}>{value}</span>
            </Tooltip>
          ) : (
            '–'
          ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => <StatusBadge status={value} />,
      },
      {
        Header: 'Latency',
        accessor: 'latency_ms',
        Cell: ({ value }) => formatLatency(value),
      },
      {
        Header: 'Error',
        accessor: 'error_message',
        Cell: ({ value }) =>
          value ? (
            <Tooltip title={value} placement='top'>
              <span className={classes.errorCell}>{value}</span>
            </Tooltip>
          ) : (
            '–'
          ),
      },
    ],
    [classes]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  const getRowClass = (status) => {
    if (status === 'error' || status === 'parse_failed') return classes.rowError;
    if (status === 'timeout') return classes.rowTimeout;
    if (status === 'archived') return classes.rowArchived;
    return '';
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const from = totalCount === 0 ? 0 : (pageIndex - 1) * pageSize + 1;
  const to = Math.min(pageIndex * pageSize, totalCount);

  if (loading) {
    return (
      <Paper className={classes.tableContainer} style={{ textAlign: 'center', padding: 40 }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper className={classes.tableContainer}>
      <TableContainer>
        <Table {...getTableProps()} size='small'>
          <TableHead>
            {headerGroups.map((hg) => (
              <TableRow {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <TableCell
                    {...col.getHeaderProps()}
                    className={classes.tableHeader}
                  >
                    {col.render('Header')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className={classes.emptyState}>
                    <div style={{ fontSize: 40, marginBottom: 8 }}>📭</div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      No SAP messages found matching your filters
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Try adjusting your date range or removing filters
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow
                    {...row.getRowProps()}
                    className={`${getRowClass(row.original.status)} ${classes.rowClickable}`}
                    onClick={() => onRowClick(row.original)}
                  >
                    {row.cells.map((cell) => (
                      <TableCell {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.paginationBar}>
        <span className={classes.paginationText}>
          {totalCount === 0
            ? 'No results'
            : `Showing ${from}–${to} of ${totalCount} results`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            variant='outlined'
            style={{ height: 32, fontSize: 13 }}
          >
            {[20, 50, 100, 200].map((s) => (
              <MenuItem key={s} value={s}>
                {s} / page
              </MenuItem>
            ))}
          </Select>
          <IconButton
            size='small'
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={pageIndex <= 1}
          >
            {'<'}
          </IconButton>
          <span className={classes.paginationText}>
            {pageIndex} / {totalPages || 1}
          </span>
          <IconButton
            size='small'
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={pageIndex >= totalPages}
          >
            {'>'}
          </IconButton>
        </div>
      </div>
    </Paper>
  );
};

export default MessageTable;
